import { config } from '@app/config';
import { useIsAdminUser } from '@app/core/auth/authentication/hooks/useIsAdminUser';
import { useIsTestingUser } from '@app/core/auth/authentication/hooks/useIsTestingUser';
import { usePostHogCapture } from '@app/core/integrations/posthog/usePostHogCapture';
import { Config, driver, Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { renderNewChipAboveTitle, replaceCloseButtonWithMuiIcon } from './driver-ui';
import { driverJsNewChip, getDriverJsLabels } from './driver.messages';
import { useToursStore } from './tours.store';

export type TourConfig = Config;

/** Tour definition: key for storage, expiry date (show until this date). */
export interface Tour {
  key: string;
  expiry: string;
}

/** Return handle for controlling the tour. */
export interface TourHandle {
  /** Closes the tour if it is active. */
  closeTour: () => void;
}

/**
 * Runs a one-time feature tour if not yet seen and before expiry.
 * featureKey is taken from tour.key. Merges localized driver.js labels and showProgress into the given config.
 * Skips starting the tour when `config.disableFeatureTours` is `true` (e.g. Playwright CI),
 * when the logged-in user is the testing account, or when the Auth0 session is the admin account.
 */
export const useFeatureTour = (tour: Tour, driverConfig: Config): TourHandle => {
  const { $t } = useIntl();
  const isAdminUser = useIsAdminUser();
  const isTestingUser = useIsTestingUser();
  const capture = usePostHogCapture({ domain: 'feature_tour' });
  const markTourAsSeen = useToursStore((state) => state.markTourAsSeen);
  const hasSeenTour = useToursStore((state) => state.hasSeenTour);
  const checkUrlActions = useToursStore((state) => state.checkUrlActions);
  const driverRef = useRef<Driver>();

  const closeTour = useCallback(() => {
    if (driverRef.current?.isActive()) {
      driverRef.current.destroy();
    }
  }, []);

  useEffect(() => {
    checkUrlActions();

    if (config.disableFeatureTours || isTestingUser || isAdminUser) {
      return;
    }

    const featureKey = tour.key;
    const hasSeen = hasSeenTour(featureKey);
    const now = new Date();
    const limitDate = new Date(tour.expiry);

    const steps = driverConfig.steps;
    const hasManySteps = steps && steps.length > 1;

    // 1. Check if we are past the promotion date
    // 2. Check if the user has already seen it
    if (now < limitDate && !hasSeen) {
      const newLabel = $t(driverJsNewChip);
      const driverObj = driver({
        ...getDriverJsLabels($t),
        showProgress: hasManySteps,
        ...driverConfig,
        allowClose: false,
        onPopoverRender: (popover, opts) => {
          renderNewChipAboveTitle(popover, newLabel);
          replaceCloseButtonWithMuiIcon(popover);
          driverConfig.onPopoverRender?.(popover, opts);
        },
        onDestroyed: (element, step, opts) => {
          capture('closed', { tour_key: featureKey });
          markTourAsSeen(featureKey);
          // Preserve caller cleanup (e.g. clearing tour-only demo UI).
          driverConfig.onDestroyed?.(element, step, opts);
        },
        showButtons: hasManySteps ? ['next', 'previous', 'close'] : ['next', 'close'],
      });

      driverRef.current = driverObj;
      capture('shown', { tour_key: featureKey });
      driverObj.drive();
    }
  }, [capture]);

  return { closeTour };
};
